// src/pages/patient/PatientTicketDetail.jsx
//
// /patient/tickets/:ticketId -- the ticket conversation.
//
//   * bubbles oldest first: the patient's own messages on the right ("You"),
//     support/clinic replies on the left with the author's name;
//   * a sequenced background refresh keeps the conversation current and never
//     touches the reply draft or its pending files; new replies that arrive
//     while the patient has scrolled up are announced with a "New replies"
//     control instead of jumping the view;
//   * every control comes from the server's allowed_actions: resolved =>
//     composer disabled + Reopen (mandatory message, focus-managed dialog);
//     closed => read-only;
//   * each composed reply has one client_request_id, reused on retry.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { apiErrorCode, readApiError } from "../../lib/apiErrors";
import { newClientRequestId } from "../../lib/clientRequestId";
import { browserTimezone, formatDateTimeInZone } from "../../lib/displayTimezone";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { patientTicketsApi } from "../../services/patientTickets";
import { InlineError, classNames } from "../../components/patient/booking/bookingUi";
import Dialog from "../../components/patient/appointments/Dialog";
import { AttachmentPicker, CategoryBadge, PriorityBadge, StatusBadge, formatBytes, usePendingUploads } from "../../components/patient/tickets/ticketUi";

export const TICKET_REFRESH_MS = 30000;
const NEAR_BOTTOM_PX = 80;
const MAX_MESSAGE = 10000;

function authorLabel(message) {
  if (message.author_role === "you") return "You";
  if (message.author_name) return message.author_name;
  return message.author_role === "clinic" ? "Clinic" : "Support team";
}

/** Oldest first. The ticket's opening description is shown first unless the server already includes it. */
export function conversationOf(ticket) {
  const messages = Array.isArray(ticket?.messages) ? [...ticket.messages] : [];
  messages.sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  const opening = ticket?.content && !(messages[0] && messages[0].message === ticket.content)
    ? [{ id: `${ticket.id}-opening`, author_role: "you", author_name: null, message: ticket.content, created_at: ticket.created_at, attachments: ticket.attachments || [] }]
    : [];
  return opening.concat(messages);
}

function AttachmentChip({ attachment, onOpen, busy }) {
  return (
    <button type="button" onClick={() => onOpen(attachment)} disabled={busy}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1 text-xs font-semibold text-text hover:bg-slate-50 disabled:opacity-60"
      aria-label={`Open attachment ${attachment.file_name}`}>
      <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m21 11-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span className="truncate">{attachment.file_name}</span>
      {attachment.size_bytes ? <span className="font-normal text-muted">{formatBytes(attachment.size_bytes)}</span> : null}
    </button>
  );
}

function Bubble({ message, timeZone, onOpenAttachment, attachmentBusy }) {
  const mine = message.author_role === "you";
  return (
    <li className={classNames("flex", mine ? "justify-end" : "justify-start")} data-testid="ticket-message" data-author={mine ? "you" : "support"}>
      <div className={classNames("max-w-[85%] rounded-3xl px-4 py-3 text-sm shadow-sm sm:max-w-[75%]", mine ? "rounded-br-md bg-primary text-white" : "rounded-bl-md border border-border bg-white text-text")}>
        <div className={classNames("flex flex-wrap items-baseline gap-x-2 text-xs", mine ? "text-white/80" : "text-muted")}>
          <span className={classNames("font-semibold", mine ? "text-white" : "text-text")}>{authorLabel(message)}</span>
          <time dateTime={message.created_at}>{formatDateTimeInZone(message.created_at, timeZone)}</time>
        </div>
        <p className="mt-1 whitespace-pre-wrap break-words">{message.message}</p>
        {message.attachments?.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {message.attachments.map((a) => <AttachmentChip key={a.id} attachment={a} onOpen={onOpenAttachment} busy={attachmentBusy} />)}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="break-words text-sm text-text">{children || "—"}</dd>
    </div>
  );
}

export default function PatientTicketDetail() {
  const { ticketId } = useParams();
  const [timeZone] = useState(() => browserTimezone() || "");
  const detail = useSequencedRequest(null);
  const runDetail = detail.run;

  const [reply, setReply] = useState("");
  const [replyError, setReplyError] = useState("");
  const [sendError, setSendError] = useState("");
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const replyRequestId = useRef(null);

  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenMessage, setReopenMessage] = useState("");
  const [reopenFieldError, setReopenFieldError] = useState("");
  const [reopenError, setReopenError] = useState("");
  const [reopening, setReopening] = useState(false);
  const reopenRequestId = useRef(null);

  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [notice, setNotice] = useState("");

  const scrollRef = useRef(null);
  const lastCount = useRef(0);
  // Measured on scroll (before new content changes the height).
  const atBottomRef = useRef(true);
  const [newReplies, setNewReplies] = useState(false);

  const uploads = usePendingUploads((file) => patientTicketsApi.uploadTicketAttachment(ticketId, file));

  const load = useCallback(() => runDetail(({ signal }) => patientTicketsApi.detail(ticketId, { signal })), [runDetail, ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  // Background refresh: replaces only the ticket data; the composer is separate state.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!sendingRef.current) load();
    }, TICKET_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const ticket = detail.data && String(detail.data.id) === String(ticketId) ? detail.data : null;
  const conversation = ticket ? conversationOf(ticket) : [];
  const count = conversation.length;

  const nearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setNewReplies(false);
  }, []);

  // New messages: follow them when the patient is at the bottom; otherwise offer "New replies".
  useLayoutEffect(() => {
    if (!count) return;
    const previous = lastCount.current;
    lastCount.current = count;
    if (previous === 0) {
      scrollToBottom();
      return;
    }
    if (count > previous) {
      if (atBottomRef.current) scrollToBottom();
      else setNewReplies(true);
    }
  }, [count, scrollToBottom]);

  function onScroll() {
    atBottomRef.current = nearBottom();
    if (newReplies && atBottomRef.current) setNewReplies(false);
  }

  async function onSend(event) {
    event.preventDefault();
    if (sendingRef.current || !ticket) return;
    const text = reply.trim();
    if (!text) {
      setReplyError("Write a message before sending.");
      document.getElementById("ticket-reply")?.focus();
      return;
    }
    if (text.length > MAX_MESSAGE) {
      setReplyError(`Keep your message under ${MAX_MESSAGE} characters.`);
      return;
    }
    if (uploads.busy) {
      setSendError("Please wait for your files to finish uploading.");
      return;
    }
    if (uploads.failed) {
      setSendError("One of your files did not upload. Retry or remove it before sending.");
      return;
    }
    // One key per composed reply, reused if this send is retried.
    if (!replyRequestId.current) replyRequestId.current = newClientRequestId();
    sendingRef.current = true;
    setSending(true);
    setSendError("");
    setReplyError("");
    detail.cancel(); // an in-flight refresh must not overwrite the reply's result
    try {
      const updated = await patientTicketsApi.addMessage(ticket.id, { message: text, client_request_id: replyRequestId.current, upload_tokens: uploads.tokens });
      replyRequestId.current = null;
      setReply("");
      uploads.reset();
      if (updated?.id) detail.setData(updated);
      else load();
      setNotice("Reply sent.");
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === "REOPEN_REQUIRED" || code === "TICKET_CLOSED") load();
      setSendError(readApiError(err, "Your reply could not be sent. Your message is kept — please try again."));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  function openReopen() {
    setReopenError("");
    setReopenFieldError("");
    setReopenOpen(true);
  }

  async function onReopen(event) {
    event.preventDefault();
    if (reopening || !ticket) return;
    const text = reopenMessage.trim();
    if (!text) {
      setReopenFieldError("Tell the support team why you are reopening this ticket.");
      document.getElementById("reopen-message")?.focus();
      return;
    }
    if (!reopenRequestId.current) reopenRequestId.current = newClientRequestId();
    setReopening(true);
    setReopenError("");
    detail.cancel();
    try {
      const updated = await patientTicketsApi.changeStatus(ticket.id, { status: "open", message: text, client_request_id: reopenRequestId.current });
      reopenRequestId.current = null;
      setReopenMessage("");
      setReopenOpen(false);
      if (updated?.id) detail.setData(updated);
      else load();
      setNotice("Ticket reopened.");
    } catch (err) {
      setReopenError(readApiError(err, "The ticket could not be reopened. Please try again."));
    } finally {
      setReopening(false);
    }
  }

  async function openAttachment(attachment) {
    if (attachmentBusy || !ticket) return;
    setAttachmentBusy(true);
    setAttachmentError("");
    // Open the tab synchronously (popup blockers), then point it at the short-lived URL.
    const win = window.open("", "_blank");
    try {
      const result = await patientTicketsApi.attachmentDownload(ticket.id, attachment.id);
      if (!result?.url) throw new Error("missing url");
      if (win) {
        win.opener = null;
        win.location.href = result.url;
      } else {
        window.location.assign(result.url);
      }
    } catch (err) {
      if (win) win.close();
      setAttachmentError(readApiError(err, "The attachment could not be opened. Please try again."));
    } finally {
      setAttachmentBusy(false);
    }
  }

  if (!ticket) {
    if (detail.error) {
      const notFound = detail.error?.response?.status === 404;
      return (
        <div className="mx-auto max-w-3xl py-10">
          {notFound ? (
            <div className="rounded-3xl border border-border bg-white p-8 text-center shadow-soft" data-testid="ticket-not-found">
              <h1 className="text-lg font-semibold">Ticket not found</h1>
              <p className="mt-2 text-sm text-muted">It may have been removed, or the link is incorrect.</p>
              <Link to="/patient/tickets" className="mt-4 inline-flex rounded-2xl border border-border px-4 py-2 text-sm font-semibold">Back to tickets</Link>
            </div>
          ) : (
            <InlineError message={readApiError(detail.error, "The ticket could not be loaded.")} onRetry={load} />
          )}
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-6xl py-4" aria-busy="true" data-testid="ticket-loading">
        <p className="sr-only" aria-live="polite">Loading ticket…</p>
        <div className="h-24 animate-pulse rounded-3xl border border-border bg-white" />
        <div className="mt-4 h-72 animate-pulse rounded-3xl border border-border bg-white" />
      </div>
    );
  }

  const actions = ticket.allowed_actions || {};
  const reasons = actions.unavailable_reasons || {};
  const canReply = Boolean(actions.can_reply);
  const canAttach = Boolean(actions.can_attach) && canReply;
  const isClosed = ticket.status === "closed";

  return (
    <div className="mx-auto w-full max-w-6xl py-2 sm:py-4">
      <Link to="/patient/tickets" className="text-sm font-semibold text-primary hover:underline">← My tickets</Link>

      <header className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryBadge value={ticket.category} label={ticket.category_label} />
            <span className="text-xs font-semibold text-muted">{ticket.ticket_number}</span>
          </div>
          <h1 className="mt-2 break-words text-2xl font-semibold text-text">{ticket.subject}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge value={ticket.status} label={ticket.status_label} />
          <PriorityBadge value={ticket.priority} label={ticket.priority_label} />
          {detail.loading ? <span className="text-xs text-muted" aria-live="polite">Refreshing…</span> : null}
        </div>
      </header>

      {notice ? <div role="status" className="mt-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</div> : null}
      {detail.error ? <div className="mt-3"><InlineError message={readApiError(detail.error, "Could not refresh this ticket.")} onRetry={load} /></div> : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <section aria-labelledby="conversation-title" className="relative rounded-3xl border border-border bg-white p-4 shadow-soft sm:p-5 lg:col-span-2">
          <h2 id="conversation-title" className="text-base font-semibold text-text">Conversation</h2>
          <p className="text-sm text-muted">Continue the discussion with the support team</p>

          <div ref={scrollRef} onScroll={onScroll} className="mt-3 max-h-[60vh] overflow-y-auto rounded-2xl bg-slate-50 p-3" data-testid="ticket-conversation" aria-live="polite">
            {conversation.length ? (
              <ol className="space-y-3">
                {conversation.map((m) => <Bubble key={m.id} message={m} timeZone={timeZone} onOpenAttachment={openAttachment} attachmentBusy={attachmentBusy} />)}
              </ol>
            ) : (
              <p className="p-4 text-center text-sm text-muted">No messages yet.</p>
            )}
          </div>
          {newReplies ? (
            <div className="-mt-12 flex justify-center">
              <button type="button" onClick={scrollToBottom} className="relative z-10 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white shadow-lg">
                New replies ↓
              </button>
            </div>
          ) : null}
          {attachmentError ? <p role="alert" className="mt-2 text-sm text-rose-700">{attachmentError}</p> : null}

          {canReply ? (
            <form onSubmit={onSend} noValidate className="mt-4 space-y-3" aria-label="Reply to this ticket">
              <div>
                <label htmlFor="ticket-reply" className="sr-only">Your reply</label>
                <textarea id="ticket-reply" rows={4} value={reply} maxLength={MAX_MESSAGE} placeholder="Write your reply..."
                  onChange={(e) => {
                    setReply(e.target.value);
                    if (replyError) setReplyError("");
                    setNotice("");
                  }}
                  aria-invalid={replyError ? "true" : undefined} aria-describedby={replyError ? "ticket-reply-error" : undefined}
                  className={classNames("w-full rounded-2xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30", replyError ? "border-rose-400" : "border-border")} />
                {replyError ? <p id="ticket-reply-error" className="mt-1 text-xs font-semibold text-rose-700">{replyError}</p> : null}
              </div>
              {canAttach ? (
                <AttachmentPicker uploads={uploads} disabled={sending} idPrefix="ticket-reply" title="Attach files" hint="Add screenshots or documents to support your reply" />
              ) : null}
              {sendError ? <p role="alert" className="text-sm text-rose-700" data-testid="reply-error">{sendError}</p> : null}
              <div className="flex justify-end">
                <button type="submit" disabled={sending || uploads.busy} aria-busy={sending || undefined}
                  className="btn-primary-gradient inline-flex items-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 12 20 4l-6 16-2.5-6.5L4 12Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                  {sending ? "Sending…" : "Send reply"}
                </button>
              </div>
            </form>
          ) : (
            <div className="mt-4 rounded-2xl border border-border bg-slate-50 p-4 text-sm text-slate-700" data-testid="composer-unavailable">
              <label htmlFor="ticket-reply-disabled" className="sr-only">Your reply</label>
              <textarea id="ticket-reply-disabled" rows={2} disabled placeholder="Write your reply..." className="w-full cursor-not-allowed rounded-2xl border border-border bg-slate-100 px-3 py-2 text-sm" />
              {actions.can_reopen ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <p>{reasons.reply?.message || "This ticket is resolved. Reopen it if the issue persists."}</p>
                  <button type="button" onClick={openReopen} className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white">Reopen ticket</button>
                </div>
              ) : (
                <p className="mt-3">
                  {reasons.reply?.message || (isClosed ? "This ticket is closed and is read-only." : "Replies are not available for this ticket.")}{" "}
                  {isClosed ? <Link to="/patient/tickets/new" className="font-semibold text-primary hover:underline">Raise a new ticket</Link> : null}
                </p>
              )}
              {!actions.can_reopen && reasons.reopen?.message && !isClosed ? <p className="mt-1 text-xs text-muted">{reasons.reopen.message}</p> : null}
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <section aria-labelledby="ticket-details-title" className="rounded-3xl border border-border bg-white p-5 shadow-soft">
            <h2 id="ticket-details-title" className="text-base font-semibold text-text">Ticket details</h2>
            <dl className="mt-2">
              <DetailRow label="Ticket">{ticket.ticket_number}</DetailRow>
              <DetailRow label="Subject">{ticket.subject}</DetailRow>
              <DetailRow label="Category">{ticket.category_label || ticket.category}</DetailRow>
              <DetailRow label="Priority">{ticket.priority_label || ticket.priority}</DetailRow>
              <DetailRow label="Status">{ticket.status_label || ticket.status}</DetailRow>
              <DetailRow label="Owner">{ticket.owner_label}</DetailRow>
              <DetailRow label="Created">{formatDateTimeInZone(ticket.created_at, timeZone)}</DetailRow>
              <DetailRow label="Updated">{formatDateTimeInZone(ticket.updated_at, timeZone)}</DetailRow>
              {ticket.appointment_ref ? (
                <DetailRow label="Appointment">
                  <Link to={`/patient/appointments/${encodeURIComponent(ticket.appointment_ref.id)}`} className="font-semibold text-primary hover:underline">
                    {ticket.appointment_ref.appointment_number || ticket.appointment_ref.id}
                  </Link>
                  {ticket.appointment_ref.service_name ? <span className="block text-xs text-muted">{ticket.appointment_ref.service_name}</span> : null}
                  <span className="block break-all font-mono text-[11px] text-muted">{ticket.appointment_ref.id}</span>
                </DetailRow>
              ) : null}
              {ticket.invoice_ref ? (
                <DetailRow label="Invoice">
                  <Link to={`/patient/invoices?invoice_id=${encodeURIComponent(ticket.invoice_ref.id)}`} className="font-semibold text-primary hover:underline">
                    {ticket.invoice_ref.invoice_number || ticket.invoice_ref.id}
                  </Link>
                </DetailRow>
              ) : null}
            </dl>
          </section>
        </aside>
      </div>

      <Dialog open={reopenOpen} title="Reopen ticket" labelId="reopen-title" busy={reopening} onClose={() => setReopenOpen(false)}>
        <form onSubmit={onReopen} noValidate>
          <label htmlFor="reopen-message" className="text-sm font-semibold text-text">Why are you reopening this ticket? <span className="text-rose-600" aria-hidden="true">*</span></label>
          <textarea id="reopen-message" data-autofocus rows={4} value={reopenMessage} maxLength={MAX_MESSAGE}
            onChange={(e) => {
              setReopenMessage(e.target.value);
              if (reopenFieldError) setReopenFieldError("");
            }}
            aria-invalid={reopenFieldError ? "true" : undefined} aria-describedby={reopenFieldError ? "reopen-message-error" : undefined}
            className={classNames("mt-1 w-full rounded-2xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30", reopenFieldError ? "border-rose-400" : "border-border")} />
          {reopenFieldError ? <p id="reopen-message-error" className="mt-1 text-xs font-semibold text-rose-700">{reopenFieldError}</p> : null}
          {reopenError ? <p role="alert" className="mt-2 text-sm text-rose-700">{reopenError}</p> : null}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setReopenOpen(false)} disabled={reopening} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text disabled:opacity-60">Cancel</button>
            <button type="submit" disabled={reopening} aria-busy={reopening || undefined} className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {reopening ? "Reopening…" : "Reopen ticket"}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
